import React, { useState, useEffect, useRef } from 'react';
import { observer } from 'mobx-react';
import styled from 'styled-components';
import { Space } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  Button, IconType, Tooltip,
} from '@routific/hive';
import { useStore } from '../../../../store/mst';
import getRouteProps from '../../../../store/mst/utils/getRoutesProps';
import RouteBreakdown from '../../RoutesPanel/RouteBreakdown';
import Panel, { PanelMode } from '../../../shared/Panel';
import RouteDetailsPanelFooter from './RouteDetailsPanelFooter';
import { PanelType } from '../index';
import RouteDetailsPanelHeader from './RouteDetailsPanelHeader';
import RouteActionsButton from './RouteActionsButton';

interface Props {
  visible: boolean;
  hasTransitionedIn: boolean
  isNextViewOrders: boolean
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  min-height: 0;
  overflow: hidden;
`;

const ConditionalWrapper = styled.div`
  height: 100%;
  position: relative;
  `;

const getTransitionClass = (visible: boolean, hasTransitionedIn: boolean, isNextViewOrders: boolean, previousDetailsView?: PanelType) => {
  let transitionClass = '';

  if (visible && hasTransitionedIn) {
    transitionClass = 'slide-in-left';
    if (previousDetailsView === PanelType.ORDER_DETAILS) return '';
  } else {
    transitionClass = 'slide-out-right';
    if (isNextViewOrders) transitionClass = 'slide-out-left';
  }

  return transitionClass;
};

const RouteDetailsPanel: React.FC<Props> = ({ visible, hasTransitionedIn, isNextViewOrders }) => {
  const store = useStore();
  const { ui, model } = store;

  const { t } = useTranslation('components');
  const { selectedRoute, previousDetailsView, selectedRouteEvent } = ui;
  const routeDetailsRef = useRef<HTMLDivElement | null>(null);
  const [hasMountedPanelMode, setHasMountedPanelMode] = useState<boolean>(() => !!selectedRoute?.timeline?.length);
  const [isRouteLoading, setIsRouteLoading] = useState<boolean>(false);

  const routeId = selectedRoute?.uuid;
  const hasTimeline = !!selectedRoute?.timeline?.length;

  useEffect(() => {
    if (model.isWorkspaceRouteLoading) return;

    setHasMountedPanelMode(hasTimeline);
    if (!hasTimeline) {
      const panelMountedTimeout = setTimeout(() => setHasMountedPanelMode(true), 250);
      return () => clearTimeout(panelMountedTimeout);
    }
  }, [routeId, hasTimeline, model.isWorkspaceRouteLoading]);

  const transitionClass = getTransitionClass(visible, hasTransitionedIn, isNextViewOrders, previousDetailsView);

  const projectId = JSON.stringify(selectedRoute?.projectId);

  const routeIsSelected = !!projectId && !!selectedRoute?.uuid;

  const handleLoading = (state: boolean) => {
    setIsRouteLoading(state);
  };

  if (!selectedRoute) {
    if (selectedRouteEvent) {
      ui.selectRoute(ui.selectedRouteEvent?.route?.id, false);
    }
    return null;
  }

  const routeProps = getRouteProps([selectedRoute]);
  const overrideLoading = !hasMountedPanelMode ? true : isRouteLoading;

  const handleClose = () => ui.selectRoute(null);

  return (
    <ConditionalWrapper className={transitionClass}>
      <Panel
        title={<RouteDetailsPanelHeader route={selectedRoute} />}
        label="Route Details"
        headerContent={(
          <>
            <Space size="small" align="center">
              <RouteActionsButton
                routeProps={routeProps}
                overrideLoading={overrideLoading}
                projectId={selectedRoute.projectId}
              />
              <Tooltip title={t('unscheduledVisits.header.close')}>
                <Button data-cy="close-route-details" icon={IconType.Close} size="small" onClick={handleClose} />
              </Tooltip>
            </Space>
          </>
        )}
        footerContent={<RouteDetailsPanelFooter />}
        mode={PanelMode.OPEN}
        dataCy="route-details-panel"
        fixedWidth
        side="right"
        loading={model.isWorkspaceRouteLoading}
      >
        <Container>
          {routeIsSelected && (
            <>
              <RouteBreakdown route={selectedRoute} routeDetailsRef={routeDetailsRef} selectable={false} onLoading={handleLoading} hasMountedPanelMode={hasMountedPanelMode} />
            </>
          )}
        </Container>
      </Panel>
    </ConditionalWrapper>
  );
};

export default observer(RouteDetailsPanel);
